import { useMemo, useState } from 'react';

import { ArrowBackIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Flex,
    IconButton,
    Progress,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';

import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@/components/v4';
import ChunkResultsList from 'pages/telephony/numberBlocks/add/components/ChunkResultsList';
import OrderStep from 'pages/telephony/numberBlocks/add/components/OrderStep';
import { getResultCounts } from 'pages/telephony/numberBlocks/add/components/SubmitProgressModal.utils';
import ChunkSummaryTable from 'pages/telephony/numberBlocks/add/table/ChunkSummaryTable';
import UploadWarningAlert from 'pages/telephony/numberBlocks/add/components/UploadWarningAlert';

/*
 * Modal displayed while number blocks are being submitted and after completion.
 * Handles both single-chunk and multi-chunk (batch) upload flows.
 */
export default function SubmitProgressModal({
    isOpen,
    onClose,
    isProcessing,
    chunkProgress,
    chunkResults,
    isOrderSubmit,
    orderResult,
    onGoToOrders,
    darkMode,
}) {
    const [selectedChunk, setSelectedChunk] = useState(null);

    const handleClose = () => {
        setSelectedChunk(null);
        onClose();
    };

    const modalTitle = isOrderSubmit
        ? 'Creating Number Block Order'
        : isProcessing
          ? 'Uploading Number Blocks...'
          : selectedChunk !== null
            ? 'Group Detail'
            : 'Upload Complete';

    const isMultiChunk = chunkProgress.total > 1;
    const selectedChunkResult =
        selectedChunk === null
            ? null
            : chunkResults.find((r) => r.chunkIndex === selectedChunk);
    const selectedChunkCounts = useMemo(
        () => getResultCounts(selectedChunkResult?.items ?? []),
        [selectedChunkResult],
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
            <ModalOverlay />
            <ModalContent
                bg={darkMode ? 'gray.800' : 'white'}
                display="flex"
                flexDirection="column"
                maxH="85vh"
                overflow="hidden">
                <ModalHeader
                    color={darkMode ? 'white' : 'gray.900'}
                    flexShrink={0}>
                    <Flex align="center" gap={2}>
                        {selectedChunk !== null && !isOrderSubmit && (
                            <IconButton
                                aria-label="Back to summary"
                                icon={<ArrowBackIcon />}
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedChunk(null)}
                            />
                        )}
                        <Text as="span">{modalTitle}</Text>
                        {selectedChunkResult && (
                            <Flex gap={2} ml="auto" wrap="wrap">
                                {selectedChunkCounts.success > 0 && (
                                    <Badge
                                        borderRadius="full"
                                        colorScheme="green"
                                        textTransform="capitalize"
                                        px={2.5}
                                        py={1}>
                                        {selectedChunkCounts.success} Success
                                    </Badge>
                                )}
                                {selectedChunkCounts.failed > 0 && (
                                    <Badge
                                        borderRadius="full"
                                        colorScheme="red"
                                        textTransform="capitalize"
                                        px={2.5}
                                        py={1}>
                                        {selectedChunkCounts.failed} Failed
                                    </Badge>
                                )}
                            </Flex>
                        )}
                    </Flex>
                </ModalHeader>

                <ModalBody overflowY="auto" minH={0}>
                    {isOrderSubmit ? (
                        <VStack spacing={3} align="stretch" py={1}>
                            <OrderStep
                                state="done"
                                darkMode={darkMode}
                                label={`Requested order for ${
                                    orderResult?.total ?? 0
                                } number block${
                                    orderResult?.total === 1 ? '' : 's'
                                }`}
                            />
                            <OrderStep
                                state={
                                    isProcessing
                                        ? 'loading'
                                        : orderResult?.success
                                          ? 'done'
                                          : 'error'
                                }
                                darkMode={darkMode}
                                label="Creating Order"
                            />
                            <OrderStep
                                state={
                                    !isProcessing && orderResult?.success
                                        ? 'done'
                                        : 'pending'
                                }
                                darkMode={darkMode}
                                label="Order created"
                            />

                            {!isProcessing && !orderResult?.success && (
                                <Box
                                    bg={darkMode ? 'whiteAlpha.100' : 'red.50'}
                                    borderRadius="md"
                                    px={4}
                                    py={3}>
                                    <Text fontSize="sm" color="red.600">
                                        {orderResult?.message ??
                                            "We couldn't create your order. Please try again."}
                                    </Text>
                                </Box>
                            )}

                            {onGoToOrders &&
                                (isProcessing || orderResult?.success) && (
                                    <Box
                                        bg={
                                            darkMode
                                                ? 'whiteAlpha.100'
                                                : 'blue.50'
                                        }
                                        borderRadius="md"
                                        px={4}
                                        py={3}
                                        mt={1}>
                                        <Text
                                            fontSize="sm"
                                            color={
                                                darkMode
                                                    ? 'gray.200'
                                                    : 'gray.700'
                                            }>
                                            You can check the order status
                                            anytime under the{' '}
                                            <Text as="span" fontWeight="bold">
                                                Orders
                                            </Text>{' '}
                                            tab.
                                        </Text>
                                    </Box>
                                )}
                        </VStack>
                    ) : isMultiChunk ? (
                        <VStack spacing={4} align="stretch">
                            {isProcessing && (
                                <>
                                    <Text
                                        textAlign="center"
                                        color={
                                            darkMode ? 'gray.100' : 'gray.700'
                                        }>
                                        Processing batch {chunkProgress.current}{' '}
                                        of {chunkProgress.total}
                                    </Text>
                                    <Progress
                                        value={
                                            (chunkProgress.current /
                                                chunkProgress.total) *
                                            100
                                        }
                                        colorScheme="messenger"
                                        size="md"
                                        borderRadius="md"
                                        hasStripe
                                        isAnimated
                                    />
                                    <UploadWarningAlert />
                                </>
                            )}

                            {selectedChunk !== null ? (
                                <VStack spacing={2} align="stretch">
                                    <ChunkResultsList
                                        items={selectedChunkResult?.items ?? []}
                                        darkMode={darkMode}
                                        maxH="350px"
                                    />
                                </VStack>
                            ) : (
                                <ChunkSummaryTable
                                    chunkResults={chunkResults}
                                    darkMode={darkMode}
                                    onViewChunk={setSelectedChunk}
                                />
                            )}
                        </VStack>
                    ) : isProcessing ? (
                        <VStack spacing={4} py={4} align="stretch">
                            <VStack align="center">
                                <Spinner size="lg" />
                            </VStack>
                            <UploadWarningAlert />
                        </VStack>
                    ) : (
                        <ChunkResultsList
                            items={chunkResults[0]?.items ?? []}
                            darkMode={darkMode}
                        />
                    )}
                </ModalBody>

                {!isProcessing &&
                    (isOrderSubmit ? (
                        <ModalFooter gap={3} flexShrink={0}>
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            {orderResult?.success && onGoToOrders && (
                                <Button onClick={onGoToOrders}>
                                    Go to Orders
                                </Button>
                            )}
                        </ModalFooter>
                    ) : (
                        <ModalFooter flexShrink={0}>
                            <Button
                                variant="outline"
                                bg="white"
                                onClick={handleClose}>
                                Close
                            </Button>
                        </ModalFooter>
                    ))}
            </ModalContent>
        </Modal>
    );
}
